import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Client {
  name: string;
  property: string;
  code: string;
}

interface Props {
  client: Client;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const tabs = [
  { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
  { id: 'property', label: 'Property Details', icon: 'ri-building-line' },
  { id: 'financials', label: 'Financials', icon: 'ri-bar-chart-line' },
  { id: 'maintenance', label: 'Maintenance', icon: 'ri-tools-line' },
  { id: 'documents', label: 'Documents', icon: 'ri-file-text-line' },
];

export default function DashboardNav({ client, activeTab, onTabChange }: Props) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    sessionStorage.removeItem('hsg_client');
    navigate('/portal');
  };

  return (
    <header className="w-full bg-white border-b border-[#E8E6E3] sticky top-0 z-40">
      <div className="w-full px-6 md:px-10 h-16 flex items-center justify-between gap-6">
        {/* Logo */}
        <a href="/" className="flex-shrink-0 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/c81a63152c5e17d7d9cf1c59f52b9bbd/5d23d4ffe74dcae8c00b4613d299989b.png"
            alt="Higher Standard Global"
            className="h-8 w-auto object-contain"
          />
        </a>

        {/* Desktop Tabs */}
        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#2C2C2C] text-white'
                  : 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F5F3F0]'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Right — Client Info + Logout */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="hidden sm:flex flex-col items-end">
            <span className="text-xs font-medium text-[#1A1A1A]">{client.name}</span>
            <span className="text-xs text-[#9A9A9A] font-light">{client.property}</span>
          </div>
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#2C2C2C] text-white text-xs font-semibold flex-shrink-0">
            {client.name.split(' ').map((n) => n[0]).join('')}
          </div>
          <button
            onClick={handleLogout}
            className="hidden md:flex items-center gap-1.5 text-xs text-[#9A9A9A] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line text-sm"></i>
            Sign Out
          </button>
          {/* Mobile menu toggle */}
          <button
            className="md:hidden w-8 h-8 flex items-center justify-center text-[#2C2C2C] cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-lg ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-[#E8E6E3] px-6 py-4 flex flex-col gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { onTabChange(tab.id); setMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer text-left ${
                activeTab === tab.id
                  ? 'bg-[#2C2C2C] text-white'
                  : 'text-[#6B6B6B] hover:bg-[#F5F3F0]'
              }`}
            >
              <i className={`${tab.icon}`}></i>
              {tab.label}
            </button>
          ))}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 rounded-md cursor-pointer mt-2"
          >
            <i className="ri-logout-box-r-line"></i>
            Sign Out
          </button>
        </div>
      )}
    </header>
  );
}
